/*
 * The write-only credential entry/rotate form (U23, registry-driven since U4).
 *
 * Secrets are NEVER read back — this form only writes. The per-kind field set is
 * driven by the backend registry (`GET /credentials/kinds`): the page fetches
 * the kind descriptors and passes them in; this form renders one control per
 * field for the selected kind. The canonical write shape is the `fields`
 * field-map (per-kind), not a bare secret.
 *
 * On add (`PUT /credentials/{kind}`) or rotate (`POST /credentials/{kind}/rotate`)
 * the user supplies the plaintext field values (over TLS); afterward only the
 * masked "configured / last-rotated" state is shown.
 *
 *   - ADD: a kind <NativeSelect> + every field; required fields must be non-empty.
 *   - ROTATE: the kind is fixed (read-only); secret fields render blank with a
 *     "leave blank to keep current value" placeholder (the backend merges a
 *     partial fields map), and the public (non-secret) fields pre-fill from the
 *     credential's stored public values.
 *
 * Writes are super-admin-only (enforced server-side); the caller gates rendering
 * on `isSuperAdmin` and only mounts this form for the platform owner.
 *
 * Migrated to Mantine inputs (U6); fields carry a real `label` so the
 * registry-driven controls stay label-addressable.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  NativeSelect,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { errorMessage } from "../../api/errors";
import { ApiError } from "../../api/client";
import type {
  CredentialKindDescriptor,
  CredentialFieldDescriptor,
} from "./hooks";

export type CredentialMode =
  | { type: "add"; kind?: string }
  | { type: "rotate"; kind: string };

function credSaveError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return "Only the platform owner can add or rotate credentials.";
    }
    if (err.status === 404) {
      return "That credential no longer exists — add it instead of rotating.";
    }
  }
  return errorMessage(err);
}

/** Render one labeled control for a registry field, mapped by its type/secret. */
function FieldControl({
  field,
  value,
  invalid,
  isRotate,
  onChange,
}: {
  field: CredentialFieldDescriptor;
  value: string;
  invalid: boolean;
  isRotate: boolean;
  onChange: (next: string) => void;
}) {
  // On rotate a blank secret means "keep the current value" (server merges).
  const placeholder =
    isRotate && field.secret ? "leave blank to keep current value" : undefined;

  const label = field.label;
  const required = !isRotate && field.required;
  const description = field.secret
    ? "Write-only. Encrypted server-side and never shown again."
    : undefined;
  const error = invalid ? "This field is required." : undefined;

  if (field.type === "json") {
    return (
      <Textarea
        label={label}
        required={required}
        description={description}
        error={error}
        value={value}
        rows={6}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={placeholder}
      />
    );
  }
  return (
    <TextInput
      label={label}
      required={required}
      description={description}
      error={error}
      type={field.secret ? "password" : "text"}
      autoComplete={field.secret ? "new-password" : undefined}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={placeholder}
    />
  );
}

export function CredentialForm({
  mode,
  kinds,
  kindsLoading,
  kindsError,
  onReloadKinds,
  existingKinds,
  publicFields,
  saving,
  saveError,
  onSubmit,
  onCancel,
}: {
  mode: CredentialMode;
  /** The credential-kind descriptors from the backend registry. */
  kinds: CredentialKindDescriptor[];
  /** The kinds registry is still loading. */
  kindsLoading: boolean;
  /** The kinds registry failed to load. */
  kindsError: boolean;
  /** Retry the kinds fetch (shown on kinds-fetch error). */
  onReloadKinds?: () => void;
  /** Kinds already configured (to block a duplicate add). */
  existingKinds: string[];
  /**
   * Pre-fill values for the NON-secret fields on rotate (from the credential's
   * stored public values). Empty/undefined until resolved; a fetch failure is
   * "no prefill" and must not block submit.
   */
  publicFields?: Record<string, string>;
  saving: boolean;
  saveError: unknown;
  onSubmit: (args: {
    kind: string;
    fields: Record<string, string>;
  }) => void;
  onCancel: () => void;
}) {
  const isRotate = mode.type === "rotate";
  const [kind, setKind] = useState<string>(mode.kind ?? "");
  // Per-field plaintext values, keyed by field name. Changing the kind clears
  // these so one kind's value can't bleed into another's field.
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // On ADD, default the kind to the first descriptor once kinds resolve (only
  // if the user hasn't already picked one).
  useEffect(() => {
    if (isRotate) return;
    if (kind) return;
    if (kinds.length > 0) setKind(kinds[0].kind);
  }, [isRotate, kind, kinds]);

  const descriptor = useMemo(
    () => kinds.find((k) => k.kind === kind),
    [kinds, kind],
  );

  // Pre-fill the public (non-secret) fields on rotate once they resolve. Secret
  // fields NEVER pre-fill. We mirror the resolved public values into `values`
  // without clobbering anything the user has already typed.
  useEffect(() => {
    if (!isRotate || !descriptor || !publicFields) return;
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const field of descriptor.fields) {
        if (field.secret) continue;
        const incoming = publicFields[field.name];
        if (incoming === undefined) continue;
        if (next[field.name] === undefined) {
          next[field.name] = incoming;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isRotate, descriptor, publicFields]);

  function setFieldValue(name: string, next: string) {
    setValues((prev) => ({ ...prev, [name]: next }));
  }

  function handleKindChange(nextKind: string) {
    setKind(nextKind);
    setValues({}); // clear ALL field values on kind change
    setSubmitted(false);
  }

  const duplicateKind = !isRotate && existingKinds.includes(kind);

  // On ADD, a required field must be non-empty. On ROTATE, blanks are allowed
  // (omitted → server keeps the current value), so no required-field block.
  const requiredMissing =
    !isRotate &&
    !!descriptor &&
    descriptor.fields.some(
      (f) => f.required && !(values[f.name] ?? "").trim(),
    );

  const blocked =
    kindsLoading ||
    kindsError ||
    !descriptor ||
    duplicateKind ||
    requiredMissing;

  function buildFields(): Record<string, string> {
    if (!descriptor) return {};
    const out: Record<string, string> = {};
    for (const field of descriptor.fields) {
      const raw = values[field.name] ?? "";
      if (isRotate) {
        // Rotate: include only non-empty values; omit blanks so the backend
        // keeps the current value. Never send an empty string for a secret.
        if (raw.trim() === "") continue;
        out[field.name] = raw;
      } else {
        // Add: include everything the user entered (required ones validated).
        // Skip a blank secret so we never store an empty secret.
        if (field.secret && raw.trim() === "") continue;
        out[field.name] = raw;
      }
    }
    return out;
  }

  function handleSubmit() {
    setSubmitted(true);
    if (blocked) return;
    onSubmit({
      kind,
      fields: buildFields(),
    });
  }

  return (
    <Stack gap="md">
      {saveError != null && (
        <Alert color="red" variant="light" role="alert">
          {credSaveError(saveError)}
        </Alert>
      )}

      {isRotate ? (
        <TextInput label="Credential kind" value={mode.kind} readOnly disabled />
      ) : (
        <NativeSelect
          label="Credential kind"
          required
          value={kind}
          error={
            submitted && duplicateKind
              ? "This credential already exists — rotate it instead."
              : undefined
          }
          disabled={kindsLoading || kindsError}
          onChange={(e) => handleKindChange(e.currentTarget.value)}
          data={kinds.map((k) => ({ value: k.kind, label: k.label }))}
        />
      )}

      {kindsError ? (
        <Alert color="red" variant="light" role="alert">
          Couldn't load the credential kinds.{" "}
          {onReloadKinds && (
            <Button type="button" variant="subtle" color="gray" size="xs" onClick={onReloadKinds}>
              Retry
            </Button>
          )}
        </Alert>
      ) : kindsLoading || !descriptor ? (
        <Text fz="xs" c="dimmed">
          Loading credential fields…
        </Text>
      ) : (
        descriptor.fields.map((field) => {
          const value = values[field.name] ?? "";
          const showRequiredError =
            submitted && !isRotate && field.required && !value.trim();
          return (
            <FieldControl
              key={field.name}
              field={field}
              value={value}
              invalid={showRequiredError}
              isRotate={isRotate}
              onChange={(next) => setFieldValue(field.name, next)}
            />
          );
        })
      )}

      <Group gap="sm">
        <Button
          type="button"
          loading={saving}
          disabled={kindsLoading || kindsError || (submitted && blocked)}
          onClick={handleSubmit}
        >
          {isRotate ? "Rotate secret" : "Save credential"}
        </Button>
        <Button type="button" variant="subtle" color="gray" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}
